// Salman's Studio — the portfolio content behind each hotspot. Everything here is real: the projects and links come
// from the owner's sites and GitHub (kazisalman21), the contact details from the owner. Never add invented entries.

import { GAME } from '../features';

export const OWNER = {
  name: 'Kazi Salman',
  city: 'Dhaka',
  whatsapp: '8801948176706',
  whatsappDisplay: '+880 1948-176706',
  email: 'kazisalman840@gmail.com',
  github: 'https://github.com/kazisalman21',
};

/** The owner's CV (PDF, one page): opens in the browser's viewer; download links use the download attribute. */
export const CV = { href: '/cv/Kazi_Salman_CV.pdf', file: 'Kazi_Salman_CV.pdf' };

// The owner's Cal.com booking link (a 30-minute call). Left empty, the booking panel offers WhatsApp and email instead
// of a calendar.
export const CAL_LINK = 'kazi-salman-gt1ava/30min';

export type Project = {
  name: string;
  tag: string;
  line: string;
  stack: string[];
  links: Array<{ label: string; href: string }>;
};

export const PROJECTS: Project[] = [
  {
    name: 'Nongorr', tag: 'Live', line: 'Online store for handmade kurti, custom-size clothing and Jamdani sarees, with nationwide delivery.',
    stack: ['React 19', 'TypeScript', 'Supabase', 'PostgreSQL'], links: [{ label: 'nongorr.com', href: 'https://nongorr.com' }],
  },
  {
    name: 'Battle Zone BD', tag: 'Coming soon', line: 'Free Fire tournament platform for Bangladesh, launching soon.',
    stack: ['PHP', 'UI prototype', 'Audit'], links: [{ label: 'battlezonebd.com', href: 'https://battlezonebd.com' }],
  },
  {
    name: 'Starline', tag: 'Upcoming', line: 'Intercity bus operations platform: booking, counter POS, live GPS and QR e-tickets.',
    stack: ['React 18', 'TypeScript', 'Supabase', 'PostgreSQL'], links: [{ label: 'star-line-main.vercel.app', href: 'https://star-line-main.vercel.app' }],
  },
  {
    name: 'Personal AI', tag: 'AI', line: 'My own LLM assistant project.',
    stack: ['Python', 'LLM'], links: [{ label: 'GitHub', href: 'https://github.com/kazisalman21/Personal-Ai' }],
  },
  {
    name: 'Academic work', tag: 'Research', line: 'BRAC University, Computer Science: thesis on quality-aware AI for cervical screening support, a GNN-BERT music-context model (CSE425), a blood-donation platform (CSE470).',
    stack: ['Python', 'GNN', 'BERT', 'JavaScript'],
    links: [
      { label: 'GNN-BERT (GitHub)', href: 'https://github.com/kazisalman21/gnn-bert-music-context' },
      { label: 'Blood donation (live)', href: 'https://blood-donation-platform-one.vercel.app' },
    ],
  },
  {
    name: 'Demos', tag: 'Demo', line: 'Kuasha Resort website and the Naeem Motors & Parts clickable prototype.',
    stack: ['Astro', 'HTML'],
    links: [
      { label: 'Kuasha Resort (GitHub)', href: 'https://github.com/kazisalman21/kuasha-resort-demo' },
      { label: 'Naeem Motors (GitHub)', href: 'https://github.com/kazisalman21/naeem-motors' },
    ],
  },
];

export const SKILLS: Array<{ group: string; items: string[] }> = [
  { group: 'Web', items: ['TypeScript', 'JavaScript', 'Node.js', 'PHP', 'Astro', 'Tailwind CSS', 'PostgreSQL', 'HTML & CSS'] },
  { group: '3D & games', items: ['Three.js', 'Rapier physics', 'Blender + Python', 'Vite', 'Web Audio'] },
  { group: 'AI & research', items: ['Python', 'GNN', 'BERT', 'LLM assistants', 'Healthcare AI (thesis)'] },
];

export const WORLD_TECH: Array<[string, string]> = [
  ['Modelling', 'Every vehicle, the city and this room are procedural Blender Python scripts built to real-world sizes.'],
  ['Lighting', 'The room is lit in Cycles and baked into textures, so it renders unlit and fast in the browser.'],
  ['Driving', 'A Toyota Corolla model on Rapier physics: torque curve, 4-speed automatic, real brakes and tyre grip.'],
  ['Traffic', 'Dhaka buses, CNGs and rickshaws on lane graphs with junction rules, give-way and lane changes.'],
  ['Sound', 'The engine, tyres and wind are synthesised live with the Web Audio API.'],
];

export type PanelId = 'work' | 'skills' | 'world' | 'education' | 'about' | 'contact' | 'booking' | 'drive';
// apps of the studio computer (SalmanOS) a hotspot can open straight away
export type ComputerApp = 'home' | 'about' | 'career' | 'projects' | 'services' | 'work' | 'resume' | 'contact' | 'skills' | 'gallery' | 'terminal' | 'github';

export type Hotspot = {
  id: string;          // the HS_<Name> object in the studio GLB
  label: string;       // hover label
  panel?: PanelId;
  computer?: ComputerApp;   // sit down at the computer (SalmanOS) instead of opening a panel
  drive?: boolean;     // into the TV and out into the city: the game (while it is not live: its panel, Coming soon)
  clock?: boolean;     // the wall clock: its label and toast give the live Dhaka time
  toggle?: 'hexLights' | 'curtains';   // a switch: click to turn on / close, click again to turn off / open
  toast?: string;      // small easter eggs instead of a panel
  view: [number, number, number];   // direction from the object to the camera when focused (three.js axes)
  fill?: number;       // how much of the view height the object fills when focused
  dot?: boolean;       // show a pulsing hint dot over it
};

// three.js axes: +X out of the left wall, +Y up, +Z out of the back wall into the room
export const HOTSPOTS: Hotspot[] = [
  { id: 'HS_Monitor_Ultrawide', label: 'Use the computer', computer: 'home', view: [0.05, 0.5, 1], fill: 0.62, dot: true },
  { id: 'HS_Monitor_Code', label: 'GitHub · second screen', computer: 'github', view: [0.15, 0.45, 1], fill: 0.62 },
  { id: 'HS_PC', label: 'Use the computer', computer: 'home', view: [0.45, 0.2, 1], fill: 0.6 },
  { id: 'HS_Thesis', label: 'Education', panel: 'education', view: [0, 0, 1], fill: 0.5, dot: true },
  { id: 'HS_Pennant', label: 'BRAC University', panel: 'education', view: [1, 0, 0.15], fill: 0.45 },
  { id: 'HS_Portrait', label: 'Kazi Salman · About me', panel: 'about', view: [1, 0.05, 0.12], fill: 0.55, dot: true },
  { id: 'HS_Chair', label: 'About me', panel: 'about', view: [0.6, 0.5, 1], fill: 0.6, dot: true },
  { id: 'HS_Trophy', label: 'Battle Zone BD', panel: 'about', view: [1, 0.1, 0.25], fill: 0.35 },
  { id: 'HS_Phone', label: 'WhatsApp', panel: 'contact', view: [0, 1.4, 0.7], fill: 0.4, dot: true },
  { id: 'HS_Mail', label: 'Email', panel: 'contact', view: [0, 1.2, 0.9], fill: 0.45 },
  { id: 'HS_Calendar', label: 'Book a call', panel: 'booking', view: [0, 0, 1], fill: 0.5, dot: true },
  { id: 'HS_TV', label: GAME ? 'Play · Drive Dhaka' : 'Drive Dhaka · coming soon', drive: true, panel: 'drive', view: [1, 0.05, 0], fill: 0.55, dot: true },
  { id: 'HS_Neon', label: 'Salman’s Studio', toast: 'Salman’s Studio: Kazi Salman, software engineer, full-stack and AI engineer.', view: [1, 0, 0], fill: 0.3 },
  { id: 'HS_Rickshaw', label: 'Rickshaw toy', toast: 'Rickshaw painting of Dhaka is on UNESCO’s Intangible Cultural Heritage list (2023).', view: [1, 0.2, 0.3], fill: 0.3 },
  { id: 'HS_Poster', label: 'Jatiya Sangsad', toast: GAME ? 'Louis Kahn’s National Parliament. You can drive there: Parliament district.' : 'Louis Kahn’s National Parliament: the Parliament district is coming to the game.', view: [1, 0, 0], fill: 0.5 },
  { id: 'HS_HexLights', label: 'Hex lights · tap to switch', toggle: 'hexLights', view: [1, 0, 0], fill: 0.4 },
  { id: 'HS_Clock', label: 'Dhaka time', clock: true, view: [0, 0, 1], fill: 0.35 },
  { id: 'HS_Curtains', label: 'Curtains · tap to close', toggle: 'curtains', view: [0, 0, 1], fill: 0.6 },
  { id: 'HS_AC', label: 'Air conditioner', toast: 'Split AC, set to 24 °C: the most important machine in a Dhaka summer.', view: [0, -0.2, 1], fill: 0.3 },
  { id: 'HS_Fridge', label: 'Mini fridge', toast: 'A glass-door mini fridge: cold drinks for long build nights.', view: [0.2, 0.1, 1], fill: 0.5 },
  { id: 'HS_Flag', label: 'Bangladesh', toast: 'লাল-সবুজ: the flag of Bangladesh. A red disc on bottle green, set a little towards the hoist.', view: [1, 0.05, 0], fill: 0.5 },
  { id: 'HS_Jersey', label: 'Neymar Jr · 10', toast: 'Neymar Jr, Brazil’s number 10: a framed fan shirt.', view: [0, 0.05, 1], fill: 0.6 },
  { id: 'HS_HeroPoster', label: 'Iron Man', toast: 'Tony Stark, helmet at his feet: a framed Iron Man fan-art print.', view: [0, 0.05, 1], fill: 0.55 },
];

// owner, 2026-10-21: studying CS at BRAC University (admitted 2023), in the Fall 2026 semester, graduating in 2027
const EDUCATION_END = '2027';

export const ABOUT = [
  `I’m ${OWNER.name}, a developer from Dhaka and a Computer Science student at BRAC University, graduating in ${EDUCATION_END}.`,
  'I build web products people use: Nongorr, an online store for a clothing brand, and Battle Zone BD, a Free Fire tournament platform. I also work with AI, from my own LLM assistant to my thesis on AI for cervical screening.',
  'This city is my playground: everything in it was modelled, lit and coded by me, piece by piece.',
];

export const EDUCATION = {
  school: 'BRAC University',
  place: 'Merul Badda, Dhaka',
  degree: 'B.Sc. in Computer Science',
  status: `Fall 2026 semester · expected graduation ${EDUCATION_END}`,
  thesis: 'Quality-aware AI for cervical screening support',
  thesisId: 'T26101238',
  courses: ['CSE425 · GNN-BERT music-context understanding', 'CSE470 · Blood-donation platform', 'CSE423 · Computer graphics labs', 'CSE370 · Database systems'],
};
